/**
 * Base API Client
 *
 * Provides a foundation for all API service classes.
 * Uses native fetch for HTTP requests.
 *
 * Features:
 * - Token-based authentication
 * - Consistent error handling
 * - SSE streaming support
 */

import { createParser, type EventSourceParser } from 'eventsource-parser';
import { ASSESSMENT_SESSION_TOKEN_HEADER } from '@evaltick/shared';
import { isGuestToken, stripGuestTokenPrefix } from '@/utils/guestSession';

// =============================================================================
// TYPES
// =============================================================================

export interface RequestOptions {
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Custom headers to merge with defaults */
  headers?: Record<string, string>;
  /** Abort signal for request cancellation */
  signal?: AbortSignal;
}

export interface StreamOptions<T> extends RequestOptions {
  /** Callback for each SSE event */
  onEvent: (event: T) => void;
  /** Callback when stream errors */
  onError?: (error: Error) => void;
  /** Callback when stream completes */
  onComplete?: () => void;
}

export interface APIError {
  message: string;
  code?: string;
  status?: number;
  details?: unknown;
}

// =============================================================================
// BASE API CLIENT
// =============================================================================

/**
 * Base class for API service classes.
 * Extend this class to create domain-specific services.
 *
 * @example
 * class UserService extends BaseAPIClient {
 *   async getProfile() {
 *     return this.get<UserProfile>('/users/me');
 *   }
 * }
 */
export class BaseAPIClient {
  protected readonly baseUrl: string;
  private readonly token: string;

  constructor(token: string) {
    this.token = token;
    this.baseUrl = import.meta.env.VITE_SERVER_URL || '';
  }

  /**
   * Get authorization headers. A guest session token (see utils/guestSession.ts)
   * goes on its own header instead of `Authorization`, matching the backend's
   * authenticateSessionToken middleware.
   */
  protected getAuthHeaders(): Record<string, string> {
    if (isGuestToken(this.token)) {
      return {
        'Content-Type': 'application/json',
        [ASSESSMENT_SESSION_TOKEN_HEADER]: stripGuestTokenPrefix(this.token),
      };
    }
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.token}`,
    };
  }

  /**
   * Make a GET request
   */
  protected async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', ...options });
  }

  /**
   * Make a POST request
   */
  protected async post<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  /**
   * Make a PATCH request
   */
  protected async patch<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  /**
   * Make a PUT request
   */
  protected async put<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  /**
   * Make a DELETE request
   */
  protected async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }

  /**
   * Make a request with error handling
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit & RequestOptions = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...(options.headers || {}),
      },
      signal: options.signal,
    });

    if (!response.ok) {
      const error = await this.parseErrorResponse(response);
      throw error;
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    return JSON.parse(text) as T;
  }

  /**
   * Stream SSE events from an endpoint
   */
  protected async stream<T>(
    endpoint: string,
    body: unknown,
    options: StreamOptions<T>
  ): Promise<void> {
    const { onEvent, onError, onComplete, signal } = options;

    try {
      const url = `${this.baseUrl}${endpoint}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        const error = await this.parseErrorResponse(response);
        throw error;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body available for streaming');
      }

      const decoder = new TextDecoder();
      let isComplete = false;

      const parser: EventSourceParser = createParser({
        onEvent: (event) => {
          try {
            if (!event.data || event.data.trim() === '') {
              return;
            }

            const eventData = JSON.parse(event.data) as T;
            onEvent(eventData);

            // Check for completion events
            const anyData = eventData as Record<string, unknown>;
            if (anyData.type === 'complete' || anyData.type === 'done') {
              isComplete = true;
              onComplete?.();
            } else if (anyData.type === 'error') {
              isComplete = true;
              onError?.(new Error(String(anyData.error || 'Stream error')));
            }
          } catch (parseError) {
            // event.data is left out: it's coach response text, which session
            // replay would record.
            console.warn('Failed to parse SSE event:', parseError);
          }
        },
      });

      try {
        while (!isComplete) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          parser.feed(chunk);
        }
      } finally {
        reader.cancel();
      }

      if (!isComplete) {
        onComplete?.();
      }
    } catch (error) {
      if (signal?.aborted) {
        // Request was intentionally cancelled
        return;
      }

      if (error instanceof Error) {
        onError?.(error);
      } else if (BaseAPIClient.isApiError(error)) {
        // parseErrorResponse (above) throws a plain APIError object, not an
        // Error instance — without this branch, its real backend message
        // (e.g. a validation error) was discarded in favor of the generic
        // fallback below. `status` rides along so callers can still branch on
        // it (e.g. a 410 for a revoked package — see useRevokedKickOut).
        onError?.(Object.assign(new Error(error.message), { status: error.status }));
      } else {
        onError?.(new Error('Unknown streaming error'));
      }
    }
  }

  /**
   * Parse error response into an APIError
   */
  private async parseErrorResponse(response: Response): Promise<APIError> {
    try {
      const body = await response.text();
      let parsed: unknown;

      try {
        parsed = JSON.parse(body);
      } catch {
        parsed = body;
      }

      if (typeof parsed === 'string') {
        return {
          message: parsed,
          status: response.status,
        };
      }

      if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>;
        return {
          message: String(obj.message || `${response.status} ${response.statusText}`),
          code: obj.code ? String(obj.code) : undefined,
          status: response.status,
          details: obj.details,
        };
      }

      return {
        message: `${response.status} ${response.statusText}`,
        status: response.status,
      };
    } catch {
      return {
        message: `${response.status} ${response.statusText}`,
        status: response.status,
      };
    }
  }

  /**
   * Parse error message from various error types
   */
  static parseErrorMsg(error: unknown): string {
    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message: unknown }).message);
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'An unknown error occurred';
  }

  /**
   * Type guard for errors thrown by the request layer (see parseErrorResponse),
   * so callers can read `error.status` without casting.
   */
  static isApiError(error: unknown): error is APIError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (error as { message: unknown }).message === 'string'
    );
  }
}

/**
 * Create a service instance with token from store
 */
export function createService<T extends BaseAPIClient>(
  ServiceClass: new (token: string) => T,
  token: string | null
): T | null {
  if (!token) {
    return null;
  }
  return new ServiceClass(token);
}

export default BaseAPIClient;
